import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, DollarSign, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { API, ITEMS_PER_PAGE, paginate, getTotalPages, errDetail } from './adminShared';

const EMPTY_FORM = { code: '', name: '', symbol: '', rate_to_usd: '', decimal_places: 2 };

export default function CurrenciesTab({ currencies, reload, authCfg }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const filtered = currencies.filter((c) =>
    c.code.toLowerCase().includes(search.toLowerCase()) ||
    c.name.toLowerCase().includes(search.toLowerCase())
  );
  const paginated = paginate(filtered, page);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const currencyData = {
        code: form.code.toUpperCase(),
        name: form.name,
        symbol: form.symbol,
        rate_to_usd: parseFloat(form.rate_to_usd),
        decimal_places: parseInt(form.decimal_places),
      };
      if (editing) {
        await axios.put(`${API}/admin/currencies/${editing.code}`, currencyData, authCfg);
        toast.success('Currency updated successfully');
      } else {
        await axios.post(`${API}/admin/currencies`, currencyData, authCfg);
        toast.success('Currency created successfully');
      }
      setDialog(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      reload();
    } catch (error) {
      toast.error(errDetail(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (code) => {
    if (code === 'USD') {
      toast.error('Cannot delete USD - it is the base currency');
      return;
    }
    setConfirm({
      title: `Delete currency "${code}"`,
      description: 'This will fail if any hospital is using this currency.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/currencies/${code}`, authCfg);
          toast.success('Currency deleted successfully');
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to delete currency'));
        }
      },
    });
  };

  return (
    <Card>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} />
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Currency Management</CardTitle>
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setForm(EMPTY_FORM); }} data-testid="add-currency-btn">
              <Plus className="w-4 h-4 mr-2" />Add Currency
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Currency' : 'Add Currency'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Currency Code (e.g., USD, KSH, EUR)</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="USD" maxLength={3} required disabled={!!editing} />
              </div>
              <div>
                <Label>Currency Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="US Dollar" required />
              </div>
              <div>
                <Label>Currency Symbol</Label>
                <Input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} placeholder="$" required />
              </div>
              <div>
                <Label>Exchange Rate to USD</Label>
                <Input type="number" step="0.0001" min="0.0001" value={form.rate_to_usd} onChange={(e) => setForm({ ...form, rate_to_usd: e.target.value })} placeholder="1.0000" required />
                <p className="text-xs text-gray-500 mt-1">1 {form.code || 'XXX'} = {form.rate_to_usd || '?'} USD</p>
              </div>
              <div>
                <Label>Decimal Places</Label>
                <Input type="number" min="0" max="4" value={form.decimal_places} onChange={(e) => setForm({ ...form, decimal_places: e.target.value })} required />
              </div>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="mb-4 bg-blue-50 border border-blue-200 p-3 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> USD is the base currency. All exchange rates are defined relative to USD. You cannot delete USD.
          </p>
        </div>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="Search currencies by code or name..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="currency-search-input" />
          </div>
          {search && <p className="text-sm text-gray-500 mt-2">Found {filtered.length} currency(ies)</p>}
        </div>
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3 text-sm font-semibold">Code</th>
              <th className="text-left p-3 text-sm font-semibold">Name</th>
              <th className="text-left p-3 text-sm font-semibold">Symbol</th>
              <th className="text-right p-3 text-sm font-semibold">Rate to USD</th>
              <th className="text-center p-3 text-sm font-semibold">Decimal Places</th>
              <th className="text-center p-3 text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((currency) => (
              <tr key={currency.code} className="border-b hover:bg-gray-50">
                <td className="p-3 text-sm font-medium">{currency.code}</td>
                <td className="p-3 text-sm">{currency.name}</td>
                <td className="p-3 text-sm">{currency.symbol}</td>
                <td className="p-3 text-sm text-right">{currency.rate_to_usd.toFixed(4)}</td>
                <td className="p-3 text-sm text-center">{currency.decimal_places}</td>
                <td className="p-3 text-center">
                  <div className="flex justify-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(currency); setForm(currency); setDialog(true); }}>
                      <Edit className="w-4 h-4 text-blue-600" />
                    </Button>
                    {currency.code !== 'USD' && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(currency.code)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{search ? 'No currencies match your search.' : 'No currencies found. Add your first currency.'}</p>
          </div>
        )}
        {filtered.length > ITEMS_PER_PAGE && (
          <Pagination currentPage={page} totalPages={getTotalPages(filtered.length)} onPageChange={setPage} />
        )}
      </CardContent>
    </Card>
  );
}
